import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import Swal from "sweetalert2";
import { apiRequest } from "../../apiServices/allApi";
import "../../../assets/css/Dashboard.css";

export default function BankDetails() {
  const [loading, setLoading] = useState(false);
  const [bankList, setBankList] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  // Form Fields
  const [bankName, setBankName] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [upiId, setUpiId] = useState("");
  const [upiHolderName, setUpiHolderName] = useState("");
  const [minLimit, setMinLimit] = useState(0);
  const [maxLimit, setMaxLimit] = useState(0);
  const [status, setStatus] = useState(true);
  const [qrFile, setQrFile] = useState(null);
  const [currentQrUrl, setCurrentQrUrl] = useState("");

  // Preview Image Modal
  const [previewImg, setPreviewImg] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "";

  const fetchBankList = async () => {
    setLoading(true);
    const access_token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${access_token}`,
    };

    try {
      const res = await apiRequest("get/allbank", "GET", null, headers);
      if (res?.data?.status === "success" || Array.isArray(res?.data?.data)) {
        setBankList(res.data.data || []);
      } else if (Array.isArray(res?.data)) {
        setBankList(res.data);
      } else {
        setBankList([]);
      }
    } catch (error) {
      console.error("Error fetching bank list:", error);
      setBankList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBankList();
  }, []);

  const resetForm = () => {
    setBankName("");
    setAccountHolderName("");
    setAccountNumber("");
    setIfscCode("");
    setUpiId("");
    setUpiHolderName("");
    setMinLimit(0);
    setMaxLimit(0);
    setStatus(true);
    setQrFile(null);
    setCurrentQrUrl("");
    setIsEditing(false);
    setEditId(null);
  };

  const handleOpenAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setIsEditing(true);
    setEditId(item._id);
    setBankName(item.bankName || "");
    setAccountHolderName(item.accountHolderName || "");
    setAccountNumber(item.accountNumber || "");
    setIfscCode(item.ifscCode || "");
    setUpiId(item.upiId || "");
    setUpiHolderName(item.upiHolderName || "");
    setMinLimit(item.minLimit ?? 0);
    setMaxLimit(item.maxLimit ?? 0);
    setStatus(typeof item.status !== "undefined" ? Boolean(item.status) : true);
    setQrFile(null);
    setCurrentQrUrl(item.qrcodeImage || "");
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("bankName", bankName);
    formData.append("accountHolderName", accountHolderName);
    formData.append("accountNumber", accountNumber);
    formData.append("ifscCode", ifscCode);
    formData.append("upiId", upiId);
    formData.append("upiHolderName", upiHolderName);
    formData.append("minLimit", minLimit);
    formData.append("maxLimit", maxLimit);
    formData.append("status", status);

    if (qrFile) {
      formData.append("qrcodeImage", qrFile);
    }

    const access_token = localStorage.getItem("token");
    const headers = {
      Authorization: `Bearer ${access_token}`,
    };

    try {
      let res;
      if (isEditing && editId) {
        res = await apiRequest(`edit/bank/${editId}`, "PUT", formData, headers);
      } else {
        res = await apiRequest("bankdetail", "POST", formData, headers);
      }

      if (res?.data?.status === "success") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: isEditing
            ? "Bank detail updated successfully!"
            : "Bank detail added successfully!",
        });
        setShowModal(false);
        resetForm();
        fetchBankList();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.data?.message || "Failed to save bank detail.",
        });
      }
    } catch (error) {
      console.error("Error saving bank detail:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error?.response?.data?.message ||
          error.message ||
          "Something went wrong.",
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this bank account?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      const access_token = localStorage.getItem("token");
      const headers = {
        Authorization: `Bearer ${access_token}`,
      };

      try {
        const res = await apiRequest(`delete/bank/${id}`, "PUT", {}, headers);
        if (res?.data?.status === "success") {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "Bank detail has been deleted.",
          });
          fetchBankList();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res?.data?.message || "Failed to delete.",
          });
        }
      } catch (error) {
        console.error("Error deleting bank detail:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete bank detail.",
        });
      }
    }
  };

  return (
    <>
      <div className="admin-main-card mt-4 mb-5">
        <div className="admin-card-header">
          <h4 className="admin-card-title">
            <i className="mdi mdi-bank text-primary"></i> Bank Details & UPI
            Accounts
          </h4>
          <button className="btn btn-primary-gradient" onClick={handleOpenAdd}>
            <i className="mdi mdi-plus-circle mr-1"></i> Add Bank Account
          </button>
        </div>

        <div className="card-body p-0 bg-white">
          {loading ? (
            null
          ) : (
            <div className="table-responsive bank-list-scroll">
              <table className="table table-hover table-custom-light mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>QR Code</th>
                    <th>Bank</th>
                    <th>Name</th>
                    <th>Account</th>
                    <th>IFSC</th>
                    <th>UPI ID</th>
                    <th>UPI Name</th>
                    <th>Min - Max</th>
                    <th>Status</th>
                    <th>Added By</th>
                    <th className="text-right pr-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bankList && bankList.length > 0 ? (
                    bankList.map((item, index) => (
                      <tr key={item._id || index}>
                        <td>{index + 1}</td>
                        <td>
                          {item.qrcodeImage ? (
                            <img
                              src={`${baseUrl}/${item.qrcodeImage}`}
                              alt="QR Code"
                              style={{
                                width: "42px",
                                height: "42px",
                                objectFit: "cover",
                                cursor: "pointer",
                                borderRadius: "6px",
                                border: "1px solid #cbd5e1",
                              }}
                              onClick={() => {
                                setPreviewImg(`${baseUrl}/${item.qrcodeImage}`);
                                setShowPreview(true);
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://cdn1.vectorstock.com/i/1000x1000/94/00/selected-stamp-vector-9519400.jpg";
                              }}
                            />
                          ) : (
                            <span className="badge badge-secondary">No QR</span>
                          )}
                        </td>
                        <td>
                          <span className="font-weight-bold text-dark">
                            {item.bankName || "-"}
                          </span>
                        </td>
                        <td>{item.accountHolderName || "-"}</td>
                        <td>
                          <code>{item.accountNumber || "-"}</code>
                        </td>
                        <td>{item.ifscCode || "-"}</td>
                        <td className="text-primary font-weight-bold">
                          {item.upiId || "-"}
                        </td>
                        <td>{item.upiHolderName || "-"}</td>
                        <td>
                          <span className="text-success font-weight-bold">
                            ₹{item.minLimit ?? 0}
                          </span>
                          <span className="text-muted mx-1">-</span>
                          <span className="text-primary font-weight-bold">
                            ₹{item.maxLimit ?? 0}
                          </span>
                        </td>
                        <td>
                          {item.status ? (
                            <span className="badge badge-success px-2 py-1">
                              ACTIVE
                            </span>
                          ) : (
                            <span className="badge badge-danger px-2 py-1">
                              INACTIVE
                            </span>
                          )}
                        </td>
                        <td>{item.addedBy?.Name || "Admin"}</td>
                        <td className="text-right pr-4">
                          <button
                            className="btn btn-sm btn-outline-primary mr-2"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit"
                          >
                            <i className="mdi mdi-pencil mr-1"></i> Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(item._id)}
                            title="Delete"
                          >
                            <i className="mdi mdi-delete mr-1"></i> Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="12" className="text-center py-5 text-muted">
                        <i
                          className="mdi mdi-bank-off text-muted mb-2 d-block"
                          style={{ fontSize: "36px" }}
                        ></i>
                        No bank accounts found. Click{" "}
                        <strong>Add Bank Account</strong> to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Modal.Title className="h5 font-weight-bold text-dark">
            <i
              className={`mdi ${isEditing ? "mdi-pencil-box" : "mdi-plus-box"} text-primary mr-2`}
            ></i>
            {isEditing ? "Edit Bank & UPI Details" : "Add Bank & UPI Details"}
          </Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body style={{ backgroundColor: "#f8fafc" }}>
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">Bank Name</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="e.g. State Bank of India"
                  value={bankName}
                  onChange={(e) => setBankName(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">
                  Account Holder Name
                </label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="e.g. John Doe"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">Account Number</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="Account Number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">IFSC Code</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="e.g. SBIN0001234"
                  value={ifscCode}
                  onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">UPI ID / VPA</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="e.g. merchant@icici"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">UPI Display Name</label>
                <input
                  type="text"
                  className="form-control form-control-light"
                  placeholder="UPI Account Name"
                  value={upiHolderName}
                  onChange={(e) => setUpiHolderName(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">
                  Minimum Transaction (₹)
                </label>
                <input
                  type="number"
                  className="form-control form-control-light"
                  placeholder="Min Limit"
                  value={minLimit}
                  onChange={(e) => setMinLimit(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">
                  Maximum Transaction (₹)
                </label>
                <input
                  type="number"
                  className="form-control form-control-light"
                  placeholder="Max Limit"
                  value={maxLimit}
                  onChange={(e) => setMaxLimit(e.target.value)}
                />
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">Account Status</label>
                <select
                  className="form-control form-control-light"
                  value={status}
                  onChange={(e) => setStatus(e.target.value === "true")}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="settings-label mb-2">QR Code Image</label>
                <input
                  type="file"
                  accept="image/*"
                  className="form-control form-control-light"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setQrFile(e.target.files[0]);
                    }
                  }}
                />
                {currentQrUrl && !qrFile && (
                  <small className="text-primary d-block mt-2">
                    Current QR: {currentQrUrl}
                  </small>
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer
            style={{
              backgroundColor: "#ffffff",
              borderTop: "1px solid #e2e8f0",
            }}
          >
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <button type="submit" className="btn btn-primary-gradient px-4">
              {isEditing ? "Update Account" : "Save Account"}
            </button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* QR Code Preview Modal */}
      <Modal show={showPreview} onHide={() => setShowPreview(false)} centered>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Modal.Title className="h5 text-dark">QR Code Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body
          className="text-center p-4"
          style={{ backgroundColor: "#ffffff" }}
        >
          {previewImg && (
            <img
              src={previewImg}
              alt="QR Code Preview"
              style={{
                maxWidth: "100%",
                maxHeight: "380px",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
